import { Link } from 'react-router-dom';
import Icon from '../ui/Icon';
import { copyText } from '../../utils/clipboard';
import { formatAmsterdam } from './amsterdamTime';
import { describeCurrentPrice, formatPrice } from './scanAdapter';
import { differsFromOcr, parseNumber } from './scanRules';

/** Big "€ 2,429 /L" from the form, what OCR read when it differs, and the station's current price. */
export function PriceCard({ scan, form, fuelLabel }) {
  const price = parseNumber(form.unitPrice);
  const unit = scan.fuelOption?.unit || 'L';
  const changed = differsFromOcr(form, scan, 'unitPrice');
  const current = describeCurrentPrice(scan.currentPrice);

  return (
    <section aria-label="Extracted scan price" className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="rounded-xl bg-surface-container-low px-5 py-4 min-w-[240px]">
          <p className="text-sm text-on-surface-variant">Extracted Scan Price</p>
          <p className="mt-1 flex items-baseline gap-1">
            <span className="text-4xl font-bold text-primary font-data-mono tracking-tight" data-testid="price-big">
              {typeof price === 'number' && !Number.isNaN(price) ? formatPrice(price) : '—'}
            </span>
            <span className="text-sm text-on-surface-variant">/{unit}</span>
          </p>
          {changed && (
            <p className="mt-1.5 text-xs text-on-surface-variant">
              OCR read <span className="font-data-mono">{formatPrice(scan.ocr.unitPrice)}</span>
              {scan.userEdited ? ' · changed by user' : ' · changed in review'}
            </p>
          )}
          {!changed && scan.userEdited && <p className="mt-1.5 text-xs text-on-surface-variant">User changed what OCR read</p>}
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2 text-sm">
          <p className="text-on-surface-variant">
            Fuel ID: <span className="font-data-mono text-on-surface">{scan.fuelOption ? `${scan.fuelOption.id} (${fuelLabel ?? scan.fuelOption.name})` : '—'}</span>
          </p>
          <p className="flex items-center gap-1.5 text-xs text-on-surface-variant">
            <Icon name="local_gas_station" size={16} />
            {current ? (
              <span>
                Station now: <span className="font-data-mono text-on-surface">{current}</span>
              </span>
            ) : (
              'No price stored yet'
            )}
          </p>
        </div>
      </div>
    </section>
  );
}

function Tile({ label, children }) {
  return (
    <div className="rounded-xl bg-surface-container-low px-4 py-3 min-w-0">
      <p className="text-xs text-on-surface-variant">{label}</p>
      <div className="mt-1 text-sm text-on-surface">{children}</div>
    </div>
  );
}

/** Who submitted the scan, their scan history and a link to all their scans. */
export function UserInfoCard({ scan }) {
  const { user, userStats: stats } = scan;
  const dash = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

  return (
    <section aria-labelledby="scan-user-title" className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-5 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="scan-user-title" className="flex items-center gap-2 text-lg font-bold text-on-surface">
          <Icon name="badge" size={22} className="text-primary" />
          User Info
        </h2>
        {user.userId && <span className="px-2.5 py-1 rounded-lg bg-surface-container-low font-data-mono text-xs text-on-surface-variant">User ID: {user.userId}</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="User Details">
          <p className="font-bold truncate">{user.name || '—'}</p>
          {user.email && (
            <p className="flex items-center gap-1 text-xs text-on-surface-variant">
              <span className="font-data-mono truncate">{user.email}</span>
              <button
                type="button"
                onClick={() => copyText(user.email, 'Email copied')}
                aria-label={`Copy email ${user.email}`}
                className="p-0.5 rounded hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
              >
                <Icon name="content_copy" size={14} />
              </button>
            </p>
          )}
        </Tile>
        <Tile label="Lifetime Scans">
          <p className="font-data-mono font-bold" data-testid="user-verified">
            {dash(stats?.approved)} verified
          </p>
          <p className="text-xs text-on-surface-variant">
            {dash(stats?.rejected)} rejections · {dash(stats?.pending)} pending
          </p>
        </Tile>
        <Tile label="Submission Time">
          <p className="font-data-mono">{formatAmsterdam(scan.submittedAt)}</p>
        </Tile>
      </div>
      {user.userId && (
        <Link
          to={`/scan-details?${new URLSearchParams({ userId: user.userId, status: 'all' })}`}
          className="self-start inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          Full user ledger ({dash(stats?.submitted)} scans)
          <Icon name="chevron_right" size={18} />
        </Link>
      )}
    </section>
  );
}
